import React, { useState } from 'react';
import {
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { Download, Smartphone, Facebook, Instagram, Twitter } from 'lucide-react';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { categoryService, Category } from '../../services/categoryService';
import logoImage from '../../assets/logo.png';
import { getGiftShopUrl, getMainSiteUrl, isGiftShopDomain } from '../../utils/domainUtils';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [isExpanded, setIsExpanded] = useState(false);
  const { isInstallable, isInstalled, isIOS, installPWA } = usePWAInstall();
  const [isInstalling, setIsInstalling] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);

  React.useEffect(() => {
    const loadCategories = async () => {
      try {
        const activeCategories = await categoryService.getActiveCategories();
        // Take top 4 or whatever makes sense
        setCategories(activeCategories.slice(0, 6));
      } catch (error) {
        console.error('Error loading footer categories:', error);
      }
    };
    loadCategories();
  }, []);

  const handleInstallApp = async () => {
    console.log('Footer install button clicked'); // Debug log
    setIsInstalling(true);
    try {
      const success = await installPWA();
      console.log('Footer install result:', success); // Debug log
    } catch (error) {
      console.error('Error installing app:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const footerLinks = {
    company: [
      { name: 'About Us', href: '/about' },
      { name: 'Our Expert Guides', href: '/guides' },
      { name: 'Gift Shop', href: '/gift-shop' },
      { name: 'Careers', href: '/careers' },
      { name: 'Press', href: '/press' },
    ],
    tours: categories.length > 0
      ? categories.map(cat => ({
        name: `${cat.name} Tours`,
        href: `/category/${cat.id}`
      }))
      : [
        { name: 'Safari Tours', href: '/safari-tours' },
        { name: 'Cultural Experiences', href: '/cultural-experiences' },
        { name: 'Adventure Tours', href: '/adventure-tours' },
        { name: 'Photography Tours', href: '/photography-tours' },
      ],
    support: [
      { name: 'Help Center', href: '/help' },
      { name: 'Booking Guide', href: '/booking-guide' },
      { name: 'Travel Tips', href: '/travel-tips' },
      { name: 'FAQ', href: '/faq' },
      { name: 'Admin Access', href: '/admin-access' },
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Cancellation Policy', href: '/cancellation' },
      { name: 'Insurance', href: '/insurance' },
    ],
  };

  const getHref = (href: string) => {
    if (href === '/gift-shop') {
      return isGiftShopDomain() ? '/' : getGiftShopUrl();
    }
    return isGiftShopDomain() ? getMainSiteUrl(href) : href;
  };

  const getTarget = (href: string) => {
    return href === '/gift-shop' && !isGiftShopDomain() ? '_blank' : undefined;
  };

  return (
    <footer className="bg-primary-900 text-white relative overflow-hidden particles">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-800 to-primary-600 animate-gradient"></div>
      </div>

      {/* Mobile: Collapsible Footer */}
      <div className="md:hidden relative z-[40]">
        {/* Thin One-Line Footer - Always Visible */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-3 flex items-center justify-between border-t border-white/10 bg-primary-900 transition-all duration-300 hover:bg-primary-800 relative z-[41] pointer-events-auto"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-primary-700/50 border border-primary-500/50 flex items-center justify-center overflow-hidden flex-shrink-0">
              <img
                src={logoImage}
                alt="Maasai SightTrails Logo"
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const parent = e.currentTarget.parentElement;
                  if (parent && !parent.querySelector('.logo-fallback')) {
                    const fallback = document.createElement('div');
                    fallback.className = 'logo-fallback bg-primary-600 text-white font-bold text-xs flex items-center justify-center w-full h-full rounded-full';
                    fallback.textContent = 'MS';
                    parent.appendChild(fallback);
                  }
                }}
              />
            </div>
            <div className="text-left">
              <p className="text-xs font-semibold text-white">SightTrail Safaris</p>
              <p className="text-[10px] text-white/60">© {currentYear} SightTrail Safaris</p>
            </div>
          </div>
          <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
            <ChevronUpIcon className="h-5 w-5 text-white/70" />
          </div>
        </button>

        {/* Expanded Content - Collapsible */}
        {isExpanded && (
          <div className="overflow-hidden border-t border-white/10 animate-fade-in-up">
            <div className="px-4 py-6 space-y-6">
              {/* Quick Links Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-primary-300">Company</h4>
                  <ul className="space-y-1">
                    {footerLinks.company.slice(0, 2).map((link) => (
                      <li key={link.name}>
                        <a
                          href={getHref(link.href)}
                          target={getTarget(link.href)}
                          className="text-xs text-white/70 hover:text-white transition-colors"
                        >
                          {link.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-primary-300">Tours</h4>
                  <ul className="space-y-1">
                    {footerLinks.tours.slice(0, 2).map((link) => (
                      <li key={link.name}>
                        <a
                          href={getHref(link.href)}
                          target={getTarget(link.href)}
                          className="text-xs text-white/70 hover:text-white transition-colors"
                        >
                          {link.name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-2 pt-2 border-t border-white/10">
                <div className="flex items-center space-x-2">
                  <MapPinIcon className="h-3.5 w-3.5 text-white/60 flex-shrink-0" />
                  <span className="text-xs text-white/70">Nairobi, Kenya</span>
                </div>
                <div className="flex items-center space-x-2">
                  <PhoneIcon className="h-3.5 w-3.5 text-white/60 flex-shrink-0" />
                  <div className="flex flex-col">
                    <a href="tel:+254708560665" className="text-xs text-white/70 hover:text-white">+254 708 560 665</a>
                    <a href="tel:+254769582779" className="text-xs text-white/70 hover:text-white">+254 769 582 779</a>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <EnvelopeIcon className="h-3.5 w-3.5 text-white/60 flex-shrink-0" />
                  <a href="mailto:sighttrailsafaris@gmail.com" className="text-xs text-white/70 truncate hover:text-white">sighttrailsafaris@gmail.com</a>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                <a href={getHref('/privacy')} className="text-xs text-white/60 hover:text-white transition-colors">
                  Privacy
                </a>
                <span className="text-white/30">•</span>
                <a href={getHref('/terms')} className="text-xs text-white/60 hover:text-white transition-colors">
                  Terms
                </a>
                <span className="text-white/30">•</span>
                <a href={getHref('/admin-access')} className="text-xs text-secondary-400 hover:text-secondary-300 transition-colors font-medium">
                  Admin Portal
                </a>
              </div>

              {/* Social Media - Mobile */}
              <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                <a href="https://www.facebook.com/profile.php?id=100089437171141" target="_blank" rel="noopener noreferrer" className="bg-white/5 p-2 rounded-lg text-white/70 hover:text-white transition-all shadow-sm" title="Facebook">
                  <Facebook className="h-5 w-5" />
                </a>
                <a href="https://www.instagram.com/sighttrails_safaris/" target="_blank" rel="noopener noreferrer" className="bg-white/5 p-2 rounded-lg text-white/70 hover:text-white transition-all shadow-sm" title="Instagram">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="https://www.tiktok.com/@sighttrails_safaris?_r=1&_t=ZS-94TxGfaPdm1" target="_blank" rel="noopener noreferrer" className="bg-white/5 p-2 rounded-lg text-white/70 hover:text-white transition-all shadow-sm" title="TikTok">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
                  </svg>
                </a>
                <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="bg-white/5 p-2 rounded-lg text-white/70 hover:text-white transition-all shadow-sm" title="X (Twitter)">
                  <Twitter className="h-5 w-5" />
                </a>
              </div>

              {/* PWA Install Button - Mobile */}
              {isInstallable && !isInstalled && (
                <div className="pt-2 border-t border-white/10">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleInstallApp();
                    }}
                    disabled={isInstalling}
                    className={`w-full text-center px-4 py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] bg-sky-400 hover:bg-sky-500 text-white ${isInstalling ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'
                      }`}
                    style={{ zIndex: 10 }}
                  >
                    {isInstalling ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        <span className="text-sm">Installing...</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        {isIOS ? (
                          <Smartphone className="h-4 w-4 mr-2" />
                        ) : (
                          <Download className="h-4 w-4 mr-2" />
                        )}
                        <span className="text-sm">Install App</span>
                        {!isInstallable && (
                          <span className="ml-2 text-xs opacity-75">(Debug)</span>
                        )}
                      </div>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Desktop: Full Footer */}
      <div className="hidden md:block relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 md:py-12">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5 md:gap-6 lg:gap-8">
          {/* Company Info */}
          <div className="col-span-2 sm:col-span-2 md:col-span-3 lg:col-span-2 animate-fade-in-left">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3 md:mb-4">
              <div className="logo-container w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-modern overflow-hidden hover-magnify-sm animate-float-slow">
                <img
                  src={logoImage}
                  alt="Maasai SightTrails Logo"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    // If image fails, show text fallback
                    e.currentTarget.style.display = 'none';
                    const parent = e.currentTarget.parentElement;
                    if (parent && !parent.querySelector('.logo-fallback')) {
                      const fallback = document.createElement('div');
                      fallback.className = 'logo-fallback bg-primary-600 text-white font-bold text-sm sm:text-base md:text-lg flex items-center justify-center w-full h-full rounded-modern';
                      fallback.textContent = 'MS';
                      parent.appendChild(fallback);
                    }
                  }}
                />
              </div>
              <div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-white text-shadow">SightTrail Safaris</h3>
                <p className="text-neutral-300 text-xs sm:text-sm font-medium">Global East African Adventures</p>
              </div>
            </div>
            <p className="text-neutral-300 mb-3 sm:mb-4 md:mb-5 max-w-lg leading-relaxed text-xs sm:text-sm md:text-sm lg:text-base hidden sm:block">
              Experience the pinnacle of East African wildlife and witness Africa's Big Five with our expert professional guides.
              Discover the Serengeti, Masai Mara, Amboseli, and Ngorongoro through our world-class safari expeditions.
            </p>

            {/* Contact Info - Compact */}
            <div className="space-y-1.5 sm:space-y-1.5 md:space-y-2">
              <div className="flex items-center space-x-2 sm:space-x-2">
                <MapPinIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-neutral-400 flex-shrink-0" />
                <span className="text-neutral-300 text-xs sm:text-sm md:text-sm truncate">Nairobi, Kenya</span>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-2">
                <PhoneIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-neutral-400 flex-shrink-0" />
                <div className="flex flex-col text-neutral-300 text-xs sm:text-sm md:text-sm truncate">
                  <a href="tel:+254708560665" className="hover:text-white">+254 708 560 665</a>
                  <a href="tel:+254769582779" className="hover:text-white">+254 769 582 779</a>
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-2">
                <EnvelopeIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 text-neutral-400 flex-shrink-0" />
                <a href="mailto:sighttrailsafaris@gmail.com" className="text-neutral-300 text-xs sm:text-sm md:text-sm truncate hover:text-white">sighttrailsafaris@gmail.com</a>
              </div>
              <div className="pt-2 flex items-center space-x-5">
                <a href="https://www.facebook.com/profile.php?id=100089437171141" target="_blank" rel="noopener noreferrer" className="bg-white/5 hover:bg-blue-600 p-2 rounded-lg text-neutral-300 hover:text-white transition-all duration-300 hover:scale-110 shadow-lg group/icon" title="Facebook">
                  <Facebook className="h-5 w-5" />
                </a>
                <a href="https://www.instagram.com/sighttrails_safaris/" target="_blank" rel="noopener noreferrer" className="bg-white/5 hover:bg-pink-600 p-2 rounded-lg text-neutral-300 hover:text-white transition-all duration-300 hover:scale-110 shadow-lg group/icon" title="Instagram">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="https://www.tiktok.com/@sighttrails_safaris?_r=1&_t=ZS-94TxGfaPdm1" target="_blank" rel="noopener noreferrer" className="bg-white/5 hover:bg-neutral-800 p-2 rounded-lg text-neutral-300 hover:text-white transition-all duration-300 hover:scale-110 shadow-lg group/icon" title="TikTok">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
                  </svg>
                </a>
                <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="bg-white/5 hover:bg-neutral-900 p-2 rounded-lg text-neutral-300 hover:text-white transition-all duration-300 hover:scale-110 shadow-lg group/icon" title="X (Twitter)">
                  <Twitter className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Company Links */}
          <div className="animate-fade-in-up">
            <h3 className="text-xs sm:text-sm md:text-base font-semibold mb-2 sm:mb-2 md:mb-3 text-white text-shadow">Company</h3>
            <ul className="space-y-1 sm:space-y-1.5 md:space-y-2">
              {footerLinks.company.map((link, index) => (
                <li key={link.name}>
                  <a
                    href={getHref(link.href)}
                    target={getTarget(link.href)}
                    className="text-neutral-300 hover:text-white transition-all duration-300 block hover:translate-x-2 hover-text-magnify modern-focus text-xs sm:text-sm"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Tours Links */}
          <div className="animate-fade-in-up">
            <h3 className="text-xs sm:text-sm md:text-base font-semibold mb-2 sm:mb-2 md:mb-3 text-white text-shadow">Tours</h3>
            <ul className="space-y-1 sm:space-y-1.5 md:space-y-2">
              {footerLinks.tours.map((link, index) => (
                <li key={link.name}>
                  <a
                    href={getHref(link.href)}
                    target={getTarget(link.href)}
                    className="text-neutral-300 hover:text-white transition-all duration-300 block hover:translate-x-2 hover-text-magnify modern-focus text-xs sm:text-sm"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div className="animate-fade-in-up col-span-2 sm:col-span-1">
            <h3 className="text-xs sm:text-sm md:text-base font-semibold mb-2 sm:mb-2 md:mb-3 text-white text-shadow">Support</h3>
            <ul className="space-y-1 sm:space-y-1.5 md:space-y-2">
              {footerLinks.support.map((link, index) => (
                <li key={link.name}>
                  <a
                    href={getHref(link.href)}
                    target={getTarget(link.href)}
                    className="text-neutral-300 hover:text-white transition-all duration-300 block hover:translate-x-2 hover-text-magnify modern-focus text-xs sm:text-sm"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter Signup & Install App */}
        <div className="border-t border-neutral-700/50 mt-4 sm:mt-5 md:mt-6 pt-3 sm:pt-4 md:pt-5 animate-fade-in-up hidden sm:block">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4 lg:gap-6">
            {/* Newsletter Section */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4 flex-1">
              <div className="text-center md:text-left">
                <h3 className="text-sm sm:text-base font-semibold mb-1 text-white text-shadow">Stay Updated</h3>
                <p className="text-neutral-300 text-xs sm:text-sm">Get the latest safari news and exclusive offers</p>
              </div>
              <div className="flex w-full md:w-auto max-w-md">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 md:w-56 px-3 py-2 bg-neutral-800/80 backdrop-blur-sm border border-neutral-700/50 rounded-l-modern focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-white placeholder-neutral-400 text-xs sm:text-sm modern-focus"
                />
                <button className="bg-primary-600 hover:bg-primary-700 px-3 md:px-5 py-2 rounded-r-modern transition-all duration-300 font-medium text-xs sm:text-sm whitespace-nowrap hover-btn-magnify modern-btn modern-focus btn-interactive">
                  Subscribe
                </button>
              </div>
            </div>

            {/* PWA Install Button - Desktop */}
            {isInstallable && !isInstalled && (
              <div className="flex-shrink-0">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleInstallApp();
                  }}
                  disabled={isInstalling}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] bg-sky-400 hover:bg-sky-500 text-white text-sm whitespace-nowrap ${isInstalling ? 'opacity-75 cursor-not-allowed' : 'cursor-pointer'
                    }`}
                  style={{ zIndex: 10 }}
                >
                  {isInstalling ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Installing...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      {isIOS ? (
                        <Smartphone className="h-4 w-4 mr-2" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Install App
                      {!isInstallable && (
                        <span className="ml-2 text-xs opacity-75">(Debug)</span>
                      )}
                    </div>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Bar - Compact */}
        <div className="border-t border-neutral-700/50 mt-3 sm:mt-4 md:mt-5 pt-3 sm:pt-4 md:pt-5 flex flex-col md:flex-row justify-between items-center gap-2 sm:gap-2 md:gap-3 animate-fade-in-up">
          <div className="text-neutral-400 text-[10px] sm:text-xs text-center md:text-left">
            © {currentYear} SightTrail East African Safaris. All rights reserved.
          </div>
          <div className="flex flex-wrap justify-center md:justify-end gap-2 sm:gap-3 md:gap-4">
            {footerLinks.legal.map((link, index) => (
              <a
                key={link.name}
                href={getHref(link.href)}
                target={getTarget(link.href)}
                className="text-neutral-400 hover:text-white text-[10px] sm:text-xs transition-all duration-300 whitespace-nowrap hover-text-magnify modern-focus"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {link.name}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
